import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './newlogin.css';
import { Eye, EyeOff } from 'lucide-react';
import Footer from '../HomePage/Footer';
import NavigationBar from '../NavigationBar';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import GoogleProfileCompletionModal from './GoogleProfileCompletionModal';

const NewLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Login state
  const [loginData, setLoginData] = useState({ emailOrPhone: '', password: '', geoLocation: null });
  const [isArtist, setIsArtist] = useState(false);
  const [isVenue, setIsVenue] = useState(false);
  // Signup state
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'User',
    stageName: '',
    pincode: '', // Add pincode to signup state
    geoLocation: null,
  });
  // Forgot/reset password state
  const [forgotForm, setForgotForm] = useState({ email: '' });
  const [resetPassword, setResetPassword] = useState({ password: '', confirmPassword: '' });
  const [formStep, setFormStep] = useState('login'); // 'login', 'signup', 'forgot', 'reset'
  const [isActive, setIsActive] = useState(false); // for UI toggle
  // Security questions for artist forgot password
  const [artistSecurityQuestions, setArtistSecurityQuestions] = useState([]);
  const [selectedSecurityIdx, setSelectedSecurityIdx] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  // Security questions for user forgot password
  const [userSecurityQuestions, setUserSecurityQuestions] = useState([]);
  const [selectedUserSecurityIdx, setSelectedUserSecurityIdx] = useState('');
  const [userSecurityAnswer, setUserSecurityAnswer] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = React.useRef(null);
  const signupPasswordInputRef = React.useRef(null);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileModalFields, setProfileModalFields] = useState([]);
  const [profileModalInitial, setProfileModalInitial] = useState({});
  const [googleSignupCredential, setGoogleSignupCredential] = useState(null);
  // Capture the selected role at the exact moment the Google button is clicked
  // (avoids stale-closure issues with the GoogleLogin component)
  const googleSignupRoleRef = React.useRef('User');
  const [forgotEmailForm, setForgotEmailForm] = useState({ phone: '', securityQuestionIdx: '', securityAnswer: '' });

  // Security questions array for display
  const SECURITY_QUESTIONS = [
    'What was your childhood nickname?',
    'What is the name of your favorite childhood friend?',
    'What was the name of your first pet?',
    'What was the first concert you attended?',
    'What is your mother’s maiden name?',
    'What is your favorite book?',
    'What is your favorite movie?',
    'What is the name of the street you grew up on?',
    'What is your favorite food?',
    'What city were you born in?'
  ];

  // Handlers for toggling forms
  const handleSignUpClick = () => {
    setIsActive(true);
    setFormStep('signup');
  };
  const handleSignInClick = () => {
    setIsActive(false);
    setFormStep('login');
  };
  // Login handlers
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };
  // Handler for Google sign-up modal submit (must be inside component)
  const handleGoogleProfileModalSubmit = async (fields) => {
    setProfileModalInitial(fields);
    setShowProfileModal(false);
    if (!googleSignupCredential) return;
    try {
      const response = await fetch('https://api.musicalmeet.com/api/auth/google-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: googleSignupCredential,
          role: googleSignupRoleRef.current,
          fields
        })
      });
      const result = await response.json();
      if (response.ok) {
       // toast.success('Google sign-up successful');
        localStorage.setItem('token', result.token);
        localStorage.setItem('userId', result.userId);
        localStorage.setItem('email', result.email);
        localStorage.setItem('name', result.name);
        localStorage.setItem('isLoggedIn', 'true');
        if (result.role && result.role.toLowerCase() === 'artist') {
          localStorage.setItem('role', 'artist');
          const artistId = result.userId || result._id || '';
          localStorage.setItem('artist_id', artistId);
          if (result.profileComplete === false) {
            navigate(`/publicartistprofilepage/${artistId}`, {
              state: {
                incompleteProfile: true,
                userDetails: {
                  userId: result.userId,
                  email: result.email,
                  name: result.name,
                  role: result.role,
                },
              },
            });
          } else {
            navigate('/loggedInHomePageArtist', {
              state: {
                userDetails: {
                  userId: result.userId,
                  email: result.email,
                  name: result.name,
                  role: result.role,
                },
              },
            });
          }
        } else if (result.role && result.role.toLowerCase() === 'user') {
          localStorage.setItem('role', 'user');
          localStorage.removeItem('artist_id');
          const userId = result.userId || result._id || '';
          localStorage.setItem('user_id', userId);
          navigate('/loggedInHome', {
            state: {
              userDetails: {
                userId: result.userId,
                email: result.email,
                name: result.name,
                role: result.role,
              },
            },
          });
        } else if (result.role && (result.role.toLowerCase() === 'venue' || result.role.toLowerCase() === 'host')) {
          localStorage.setItem('role', 'venue');
          localStorage.removeItem('artist_id');
          localStorage.setItem('user_id', result.userId || '');
          // Resolve the real venue _id (may not exist yet for new signups)
          try {
            const vr = await fetch('https://api.musicalmeet.com/api/venues/my', {
              headers: { Authorization: `Bearer ${result.token}` },
            });
            if (vr.ok) {
              const vd = await vr.json();
              if (vd._id) localStorage.setItem('venue_id', vd._id);
            } else {
              localStorage.removeItem('venue_id');
            }
          } catch { localStorage.removeItem('venue_id'); }
          navigate('/loggedInHomeVenue', {
            state: {
              userDetails: {
                userId: result.userId,
                email: result.email,
                name: result.name,
                role: result.role,
              },
            },
          });
        }
      } else {
        toast.error(result.message || 'Google sign-up failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
    setGoogleSignupCredential(null);
  };
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    toast.info('Logging in...');
    // Determine if input is email or phone
    const input = loginData.emailOrPhone.trim();
    const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input);
    const isPhone = /^\d{10}$/.test(input); // Adjust regex for your phone format
    let loginPayload = {
      password: loginData.password,
      role: isArtist ? 'artist' : isVenue ? 'venue' : 'user',
      geoLocation: loginData.geoLocation, // Include geoLocation
    };
    if (isEmail) {
      loginPayload.email = input;
    } else if (isPhone) {
      loginPayload.phone = input;
    } else {
      toast.error('Please enter a valid email or 10-digit phone number');
      return;
    }
    try {
      const response = await fetch('https://api.musicalmeet.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginPayload),
      });
      const result = await response.json();
      if (response.ok) {
       // toast.success('Login successful');
        localStorage.setItem('token', result.token);
        localStorage.setItem('userId', result.userId);
        localStorage.setItem('email', result.email);
        localStorage.setItem('name', result.name);
        localStorage.setItem('isLoggedIn', 'true');
        if (result.role && result.role.toLowerCase() === 'artist') {
          localStorage.setItem('role', 'artist');
          const artistId = result.userId || result._id || '';
          localStorage.setItem('artist_id', artistId);
          if (result.profileComplete === false) {
            navigate(`/publicartistprofilepage/${artistId}`, {
              state: {
                incompleteProfile: true,
                userDetails: {
                  userId: result.userId,
                  email: result.email,
                  name: result.name,
                  role: result.role,
                },
              },
            });
          } else {
            navigate('/loggedInHomePageArtist', {
              state: {
                userDetails: {
                  userId: result.userId,
                  email: result.email,
                  name: result.name,
                  role: result.role,
                },
              },
            });
          }
        } else if (result.role && result.role.toLowerCase() === 'user') {
          localStorage.setItem('role', 'user');
          localStorage.removeItem('artist_id');
          // Set user_id for user logins (for WelcomePopup logic)
          const userId = result.userId || result._id || '';
          localStorage.setItem('user_id', userId);
          navigate('/loggedInHome', {
            state: {
              userDetails: {
                userId: result.userId,
                email: result.email,
                name: result.name,
                role: result.role,
              },
            },
          });
        } else if (result.role && (result.role.toLowerCase() === 'venue' || result.role.toLowerCase() === 'host')) {
          localStorage.setItem('role', 'venue');
          localStorage.removeItem('artist_id');
          localStorage.setItem('user_id', result.userId || '');
          // Resolve the real venue _id (may not exist yet for new signups)
          try {
            const vr = await fetch('https://api.musicalmeet.com/api/venues/my', {
              headers: { Authorization: `Bearer ${result.token}` },
            });
            if (vr.ok) {
              const vd = await vr.json();
              if (vd._id) localStorage.setItem('venue_id', vd._id);
            } else {
              localStorage.removeItem('venue_id');
            }
          } catch { localStorage.removeItem('venue_id'); }
          navigate('/loggedInHomeVenue', {
            state: {
              userDetails: {
                userId: result.userId,
                email: result.email,
                name: result.name,
                role: result.role,
              },
            },
          });
        }
      } else {
        toast.error(result.message || 'Login failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  // Signup handlers
  const handleSignupChange = (e) => {
    const { name, value } = e.target;
    setSignupData((prev) => ({ ...prev, [name]: value }));
  };
  const handleRoleSelect = (role) => {
    setIsArtist(role === 'Artist');
    setIsVenue(role === 'Venue');
    setSignupData((prev) => ({ ...prev, role }));
  };
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    // Name validation: only letters and spaces allowed
    const nameRegex = /^[A-Za-z ]+$/;
    if (!nameRegex.test(signupData.name)) {
      toast.error('Name must contain only letters and spaces (no numbers or special characters)');
      return;
    }
    // Stage Name validation: only letters and spaces allowed (if artist)
    if (isArtist && !nameRegex.test(signupData.stageName)) {
      toast.error('Stage Name must contain only letters and spaces (no numbers or special characters)');
      return;
    }
    // Email format validation
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRegex.test(signupData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    // Phone number validation
    if (!/^\d{10}$/.test(signupData.phone)) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    // Password validation
    const password = signupData.password;
    let passwordErrors = [];
    if (password.length < 8) {
      passwordErrors.push('at least 8 characters');
    }
    if (!/[A-Z]/.test(password)) {
      passwordErrors.push('one uppercase letter');
    }
    if (!/[0-9]/.test(password)) {
      passwordErrors.push('one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>\[\]\\/;'`~_-]/.test(password)) {
      passwordErrors.push('one special symbol');
    }
    if (passwordErrors.length > 0) {
      toast.error(`Password must contain ${passwordErrors.join(', ')}`);
      return;
    }
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    const signupPayload = {
      name: signupData.name,
      email: signupData.email,
      phone: signupData.phone,
      password: signupData.password,
      role: signupData.role,
      stageName: isArtist ? signupData.stageName : '',
      pincode: signupData.pincode,
      geoLocation: signupData.geoLocation, // Include geoLocation
    };
    try {
      toast.info('Processing your request...', { autoClose: 3000 });
      const response = await fetch('https://api.musicalmeet.com/api/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupPayload),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success('Sign Up successful');
        handleSignInClick();
      } else {
        toast.error(result.message || 'Sign Up failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  // Forgot/reset password handlers
  const handleForgotChange = (e) => {
    const { name, value } = e.target;
    setForgotForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    // Validate email is filled
    if (!forgotForm.email) {
      toast.error('Please enter your Email to proceed.');
      return;
    }
    try {
      // POST to /api/auth/forgot-password to let backend handle user/artist lookup and security questions
      const res = await fetch('https://api.musicalmeet.com/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotForm.email,
          userType: isArtist ? 'artist' : isVenue ? 'venue' : 'user',
        })
      });
      const result = await res.json();
      if (!res.ok) {
        setUserSecurityQuestions([]);
        setArtistSecurityQuestions([]);
        toast.error(result.message || 'User/Artist not found. Please check your Email.');
        return;
      }
      return;
    } catch (err) {
      toast.error('Error: ' + err.message);
      return;
    }
  };
  // Handler for artist security question verification
  const handleArtistSecurityVerify = async (e) => {
    e.preventDefault();
    if (!selectedSecurityIdx || !securityAnswer.trim()) {
      toast.error('Please select a question and provide an answer.');
      return;
    }
    try {
      const response = await fetch('https://api.musicalmeet.com/api/auth/verify-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotForm.email,
          userType: 'artist',
          securityQuestionIdx: selectedSecurityIdx,
          securityAnswer: securityAnswer.trim(),
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success('Verification successful! Please reset your password.');
        setFormStep('reset');
      } else {
        toast.error(result.message || 'Verification failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };
  // Handler for user security question verification (copy artist logic exactly)
  const handleUserSecurityVerify = async (e) => {
    e.preventDefault();
    if (!selectedUserSecurityIdx || !userSecurityAnswer.trim()) {
      toast.error('Please select a question and provide an answer.');
      return;
    }
    try {
      const response = await fetch('https://api.musicalmeet.com/api/auth/verify-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotForm.email,
          userType: 'user',
          securityQuestionIdx: selectedUserSecurityIdx,
          securityAnswer: userSecurityAnswer.trim(),
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success('Verification successful! Please reset your password.');
        setFormStep('reset');
      } else {
        toast.error(result.message || 'Verification failed');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  // Reset password handlers
  const handleResetChange = (e) => {
    const { name, value } = e.target;
    setResetPassword((prev) => ({ ...prev, [name]: value }));
  };
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (resetPassword.password !== resetPassword.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      const response = await fetch('https://api.musicalmeet.com/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotForm.email,
          newPassword: resetPassword.password,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success('Password updated successfully! Please login.');
        setFormStep('login');
        setResetPassword({ password: '', confirmPassword: '' });
        setForgotForm({ email: '' });
        setIsActive(false);
      } else {
        toast.error(result.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  const handleForgotEmailChange = (e) => {
    const { name, value } = e.target;
    setForgotEmailForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleForgotEmailSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmailForm.phone || !forgotEmailForm.securityQuestionIdx || !forgotEmailForm.securityAnswer.trim()) {
      toast.error('Please fill all fields to proceed.');
      return;
    }
    try {
      // POST to backend to recover email with phone, securityQuestionIdx, securityAnswer, and userType
      const res = await fetch('https://api.musicalmeet.com/api/auth/forgot-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: forgotEmailForm.phone,
          securityQuestionIdx: forgotEmailForm.securityQuestionIdx,
          securityAnswer: forgotEmailForm.securityAnswer.trim(),
          userType: isArtist ? 'artist' : isVenue ? 'venue' : 'user'
        })
      });
      const result = await res.json();
      if (res.ok && result.email) {
        toast.success(`Your registered email is: ${result.email}`);
        setFormStep('login');
        setForgotEmailForm({ phone: '', securityQuestionIdx: '', securityAnswer: '' });
      } else {
        toast.error(result.message || 'Could not recover email. Please check your details.');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  // Add state for reset via token
  const [resetTokenMode, setResetTokenMode] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetRole, setResetRole] = useState('');
  const [resetTokenError, setResetTokenError] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  React.useEffect(() => {
    // Detect /reset-password route with token, role, and email
    if (location.pathname === '/reset-password') {
      const token = searchParams.get('token');
      const role = searchParams.get('role');
      const email = searchParams.get('email');
      if (token && role) {
        setResetTokenMode(true);
        setResetToken(token);
        setResetRole(role);
        setResetEmail(email || ''); // allow empty email
        setFormStep('reset-token');
        setResetTokenError(email ? '' : 'Please enter your email to reset your password.');
      } else {
        setResetTokenMode(false);
        setResetTokenError('Invalid or missing reset link.');
        setFormStep('login');
      }
    }
  }, [location.pathname, searchParams]);

  // Handler for password reset via token
  const handleResetTokenSubmit = async (e) => {
    e.preventDefault();
    if (resetPassword.password !== resetPassword.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!resetToken || !resetRole) {
      toast.error('Invalid reset link.');
      return;
    }
    if (!resetEmail) {
      toast.error('Please enter your email.');
      return;
    }
    try {
      const response = await fetch('https://api.musicalmeet.com/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetToken,
          role: resetRole,
          email: resetEmail,
          newPassword: resetPassword.password,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success('Password updated successfully! Please login.');
        setFormStep('login');
        setResetPassword({ password: '', confirmPassword: '' });
        setResetTokenMode(false);
        navigate('/login');
      } else {
        toast.error(result.message || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    }
  };

  React.useEffect(() => {
    // Handle navigation from state (HomePage.js)
    if (location.state && location.state.signup) {
      setIsActive(true);
      setFormStep('signup');
      if (location.state.artist) {
        setIsArtist(true);
        setSignupData((prev) => ({ ...prev, role: 'Artist' }));
      }
    }
    // Handle navigation from query params (Footer links)
    if (location.search) {
      const params = new URLSearchParams(location.search);
      if (params.get('signup') === '1') {
        setIsActive(true);
        setFormStep('signup');
        if (params.get('artist') === '1') {
          setIsArtist(true);
          setSignupData((prev) => ({ ...prev, role: 'Artist' }));
        } else if (params.get('venue') === '1') {
          setIsVenue(true);
          setSignupData((prev) => ({ ...prev, role: 'Venue' }));
        }
      }
    }
  }, [location.state, location.search]);

  // Ask for geo location on signup and login
  React.useEffect(() => {
    if (formStep === 'signup' || formStep === 'login') {
      if (navigator.geolocation) {

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const geoLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            if (formStep === 'signup') {
              setSignupData((prev) => ({ ...prev, geoLocation }));
            } else if (formStep === 'login') {
              setLoginData((prev) => ({ ...prev, geoLocation }));
            }

          },
          (error) => {
            let errorMessage = 'Error getting location: ';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Please allow location access for better experience. You can continue.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information is unavailable. You can still continue.';
                break;
              case error.TIMEOUT:
                errorMessage = 'Location request timed out. You can still continue.';
                break;
              default:
                errorMessage = 'Could not detect location. You can still continue.';
            }
           // toast.warn(errorMessage, { autoClose: 5000 });
          },
          {
            enableHighAccuracy: true,
            timeout: 0,
            maximumAge: 0
          }
        );
      } else {
        toast.warn('Geolocation is not supported by your browser. You can still continue.', { autoClose: 5000 });
      }
    }
  }, [formStep]);

  // UI rendering

  const INP = {
    width: '100%',
    boxSizing: 'border-box',
    display: 'block',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    color: '#f1f5f9',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '0.9rem',
    padding: '12px 16px',
    outline: 'none',
    marginBottom: 14,
    transition: 'border-color 0.2s',
  };

  const ROLE_BTN = (active) => ({
    flex: 1,
    padding: '9px 10px',
    borderRadius: 50,
    border: active ? '1px solid #374EA9' : '1px solid rgba(255,255,255,0.1)',
    background: active ? '#374EA9' : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : '#64748b',
    fontFamily: 'Gilroy-semiBold, sans-serif',
    fontSize: '0.8rem',
    cursor: 'pointer',
    transition: 'all 0.18s',
    whiteSpace: 'nowrap',
  });

  const SUBMIT_BTN = {
    width: '100%',
    background: 'linear-gradient(135deg, #374EA9, #6366f1)',
    border: 'none',
    borderRadius: 50,
    color: '#fff',
    fontFamily: 'Gilroy-semiBold, sans-serif',
    fontSize: '0.97rem',
    padding: '13px',
    cursor: 'pointer',
    marginTop: 8,
    boxShadow: '0 4px 20px rgba(55,78,169,0.45)',
    transition: 'transform 0.18s',
  };

  const LNK = { color: '#818cf8', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '0.85rem', textDecoration: 'none' };
  const LBL = { color: '#64748b', fontFamily: 'Poppins, sans-serif', fontSize: '0.78rem', marginBottom: 6, display: 'block', letterSpacing: 0.3 };
  const SEL = { ...INP, cursor: 'pointer' };

  const isSignup = formStep === 'signup';
  const benefits = [
    { e: '🎵', t: 'Discover artists near you' },
    { e: '🎸', t: 'Book musicians for any event' },
    { e: '🏠', t: 'Find rehearsal spaces & studios' },
  ];

  return (
    <>
      {/* Full-page background */}
      <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img src="/loginBackground.png" alt="" aria-hidden="true"
          style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.38) saturate(1.05)', zIndex: 0 }} />
        <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(135deg,rgba(55,78,169,0.45) 0%,rgba(15,23,42,0.75) 100%)', zIndex: 1 }} />

        {/* Top-left brand */}
        <div style={{ position: 'fixed', top: 20, left: 28, zIndex: 50, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/LOGO.avif" alt="Musical Meet" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)' }} />
          <span style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '1rem', color: '#fff', letterSpacing: -0.3 }}>musical meet</span>
        </div>

        {/* Scrollable form wrapper */}
        <div style={{ position: 'relative', zIndex: 10, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 24px 48px', minHeight: '100vh', overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 460, background: 'rgba(10,15,30,0.75)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '40px 36px', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>

            {/* ── LOGIN ── */}
            {formStep === 'login' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Welcome back</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 8px', lineHeight: 1.15 }}>Sign In</h1>
                <p style={{ color: '#64748b', fontFamily: 'Poppins, sans-serif', fontSize: '0.87rem', margin: '0 0 28px' }}>
                  Don't have an account?{' '}
                  <span style={LNK} onClick={handleSignUpClick}>Sign up free</span>
                </p>

                <span style={LBL}>Sign in as</span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
                  <button type="button" style={ROLE_BTN(isArtist)} onClick={() => handleRoleSelect('Artist')}>Artist</button>
                  <button type="button" style={ROLE_BTN(!isArtist && !isVenue)} onClick={() => handleRoleSelect('User')}>User</button>
                  <button type="button" style={ROLE_BTN(isVenue)} onClick={() => handleRoleSelect('Venue')}>Studio / Space</button>
                </div>

                <form onSubmit={handleLoginSubmit}>
                  <span style={LBL}>Email or Phone</span>
                  <input type="text" name="emailOrPhone" placeholder="you@example.com or 9876543210" value={loginData.emailOrPhone} onChange={handleLoginChange} required style={INP}
                    onFocus={e => e.target.style.borderColor = '#818cf8'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />

                  <span style={LBL}>Password</span>
                  <div style={{ position: 'relative', marginBottom: 6 }}>
                    <input ref={passwordInputRef} type={showPassword ? 'text' : 'password'} name="password" placeholder="Your password" value={loginData.password} onChange={handleLoginChange} required
                      style={{ ...INP, marginBottom: 0, paddingRight: 44 }}
                      onFocus={e => e.target.style.borderColor = '#818cf8'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'} />
                    <span onClick={() => { setShowPassword(p => !p); setTimeout(() => { if (passwordInputRef.current) { const l = passwordInputRef.current.value.length; passwordInputRef.current.setSelectionRange(l,l); passwordInputRef.current.focus(); }}, 0); }}
                      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#64748b', display: 'flex' }} onMouseDown={e => e.preventDefault()}>
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </span>
                  </div>

                  <div style={{ textAlign: 'right', marginBottom: 22 }}>
                    <span style={LNK} onClick={() => setFormStep('forgot')}>Forgot password?</span>
                  </div>

                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Sign In</button>
                </form>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                  <span style={{ color: '#334155', fontFamily: 'Poppins, sans-serif', fontSize: '0.78rem' }}>or continue with</span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <GoogleLogin
                    onSuccess={async (credentialResponse) => {
                      try {
                        const response = await fetch('https://api.musicalmeet.com/api/auth/google', {
                          method: 'POST', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ credential: credentialResponse.credential })
                        });
                        const result = await response.json();
                        if (response.ok) {
                          localStorage.setItem('token', result.token);
                          localStorage.setItem('userId', result.userId);
                          localStorage.setItem('email', result.email);
                          localStorage.setItem('name', result.name);
                          localStorage.setItem('isLoggedIn', 'true');
                          if (result.role?.toLowerCase() === 'artist') {
                            localStorage.setItem('role', 'artist');
                            const aid = result.userId || result._id || '';
                            localStorage.setItem('artist_id', aid);
                            if (result.profileComplete === false) {
                              navigate(`/publicartistprofilepage/${aid}`, { state: { incompleteProfile: true, userDetails: { userId: result.userId, email: result.email, name: result.name, role: result.role } } });
                            } else {
                              navigate('/loggedInHomePageArtist', { state: { userDetails: { userId: result.userId, email: result.email, name: result.name, role: result.role } } });
                            }
                          } else if (result.role?.toLowerCase() === 'user') {
                            localStorage.setItem('role', 'user');
                            localStorage.removeItem('artist_id');
                            localStorage.setItem('user_id', result.userId || result._id || '');
                            navigate('/loggedInHome', { state: { userDetails: { userId: result.userId, email: result.email, name: result.name, role: result.role } } });
                          } else if (result.role?.toLowerCase() === 'venue' || result.role?.toLowerCase() === 'host') {
                            localStorage.setItem('role', 'venue');
                            localStorage.removeItem('artist_id');
                            localStorage.setItem('user_id', result.userId || '');
                            try { const vr = await fetch('https://api.musicalmeet.com/api/venues/my', { headers: { Authorization: `Bearer ${result.token}` } }); if (vr.ok) { const vd = await vr.json(); if (vd._id) localStorage.setItem('venue_id', vd._id); } else { localStorage.removeItem('venue_id'); } } catch { localStorage.removeItem('venue_id'); }
                            navigate('/loggedInHomeVenue', { state: { userDetails: { userId: result.userId, email: result.email, name: result.name, role: result.role } } });
                          }
                        } else { toast.error(result.message || 'Google sign-in failed'); }
                      } catch (err) { toast.error('Error: ' + err.message); }
                    }}
                    onError={() => toast.error('Google sign-in failed')}
                    width="100%"
                  />
                </div>
              </div>
            )}

            {/* ── SIGNUP ── */}
            {formStep === 'signup' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Get started</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 8px', lineHeight: 1.15 }}>Create Free Account</h1>
                <p style={{ color: '#64748b', fontFamily: 'Poppins, sans-serif', fontSize: '0.87rem', margin: '0 0 28px' }}>
                  Already have an account?{' '}
                  <span style={LNK} onClick={handleSignInClick}>Sign in</span>
                </p>

                <span style={LBL}>I am a</span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
                  <button type="button" style={ROLE_BTN(isArtist)} onClick={() => handleRoleSelect('Artist')}>Artist</button>
                  <button type="button" style={ROLE_BTN(!isArtist && !isVenue)} onClick={() => handleRoleSelect('User')}>User</button>
                  <button type="button" style={ROLE_BTN(isVenue)} onClick={() => handleRoleSelect('Venue')}>Studio / Space</button>
                </div>

                <form onSubmit={handleSignupSubmit}>
                  <span style={LBL}>Full Name (as per ID)</span>
                  <input type="text" name="name" placeholder="John Doe" value={signupData.name} onChange={handleSignupChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />

                  {isArtist && (
                    <>
                      <span style={LBL}>Stage Name (publicly known as)</span>
                      <input type="text" name="stageName" placeholder="Your stage name" value={signupData.stageName} onChange={handleSignupChange} required style={INP}
                        onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                    </>
                  )}

                  <span style={LBL}>Email</span>
                  <input type="email" name="email" placeholder="you@example.com" value={signupData.email} onChange={handleSignupChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />

                  <span style={LBL}>Phone</span>
                  <input type="text" name="phone" placeholder="10-digit mobile number" value={signupData.phone} onChange={handleSignupChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />

                  <span style={LBL}>Pincode</span>
                  <input type="text" name="pincode" placeholder="Area pincode" value={signupData.pincode} onChange={handleSignupChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />

                  <span style={LBL}>Password</span>
                  <div style={{ position: 'relative', marginBottom: 14 }}>
                    <input ref={signupPasswordInputRef} type={showSignupPassword ? 'text' : 'password'} name="password" placeholder="Min 8 chars, uppercase, number, symbol" value={signupData.password} onChange={handleSignupChange} required
                      style={{ ...INP, marginBottom: 0, paddingRight: 44 }}
                      onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                    <span onClick={() => { setShowSignupPassword(p => !p); setTimeout(() => { if (signupPasswordInputRef.current) { const l = signupPasswordInputRef.current.value.length; signupPasswordInputRef.current.setSelectionRange(l,l); signupPasswordInputRef.current.focus(); }}, 0); }}
                      style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#64748b', display: 'flex' }} onMouseDown={e => e.preventDefault()}>
                      {showSignupPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </span>
                  </div>

                  <span style={LBL}>Confirm Password</span>
                  <input type="password" name="confirmPassword" placeholder="Re-enter password" value={signupData.confirmPassword} onChange={handleSignupChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />

                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Create Account</button>
                </form>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0' }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                  <span style={{ color: '#334155', fontFamily: 'Poppins, sans-serif', fontSize: '0.78rem' }}>or sign up with</span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <GoogleLogin
                    text="signup_with"
                    onSuccess={credentialResponse => {
                      googleSignupRoleRef.current = isArtist ? 'Artist' : isVenue ? 'Venue' : 'User';
                      let missingFields = ['phone', 'pincode'];
                      if (isArtist) missingFields.push('stageName');
                      setProfileModalFields(missingFields);
                      setShowProfileModal(true);
                      setGoogleSignupCredential(credentialResponse.credential);
                    }}
                    onError={() => toast.error('Google sign-up failed')}
                    width="100%"
                  />
                </div>
              </div>
            )}

            {/* ── FORGOT PASSWORD ── */}
            {formStep === 'forgot' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Account recovery</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 8px', lineHeight: 1.15 }}>Forgot Password</h1>
                <p style={{ color: '#64748b', fontFamily: 'Poppins, sans-serif', fontSize: '0.87rem', margin: '0 0 28px' }}>Enter your registered email to continue.</p>

                <span style={LBL}>I am a</span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
                  <button type="button" style={ROLE_BTN(isArtist)} onClick={() => handleRoleSelect('Artist')}>Artist</button>
                  <button type="button" style={ROLE_BTN(!isArtist)} onClick={() => handleRoleSelect('User')}>User</button>
                </div>
                <form onSubmit={handleForgotSubmit}>
                  <span style={LBL}>Email</span>
                  <input type="email" name="email" placeholder="you@example.com" value={forgotForm.email} onChange={handleForgotChange} style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <p style={{ color: '#64748b', fontFamily: 'Poppins, sans-serif', fontSize: '0.82rem', margin: '-6px 0 16px' }}>We'll send you to the next verification step.</p>
                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Continue</button>
                </form>
                <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
                  <span style={LNK} onClick={() => setFormStep('login')}>← Back to Sign In</span>
                  <span style={{ ...LNK, color: '#64748b' }} onClick={() => setFormStep('forgot-email')}>Forgot your email?</span>
                </div>
              </div>
            )}

            {/* ── FORGOT EMAIL ── */}
            {formStep === 'forgot-email' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Email recovery</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 8px', lineHeight: 1.15 }}>Forgot Email</h1>
                <p style={{ color: '#64748b', fontFamily: 'Poppins, sans-serif', fontSize: '0.87rem', margin: '0 0 28px' }}>Recover your account email using your phone number.</p>

                <span style={LBL}>I am a</span>
                <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
                  <button type="button" style={ROLE_BTN(isArtist)} onClick={() => handleRoleSelect('Artist')}>Artist</button>
                  <button type="button" style={ROLE_BTN(!isArtist)} onClick={() => handleRoleSelect('User')}>User</button>
                </div>
                <form onSubmit={handleForgotEmailSubmit}>
                  <span style={LBL}>Mobile Number</span>
                  <input type="text" name="phone" placeholder="10-digit number" value={forgotEmailForm.phone} onChange={handleForgotEmailChange} style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <span style={LBL}>Security Question</span>
                  <select name="securityQuestionIdx" value={forgotEmailForm.securityQuestionIdx} onChange={handleForgotEmailChange} required style={SEL}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'}>
                    <option value="">Select a security question</option>
                    {SECURITY_QUESTIONS.map((q, i) => <option key={i} value={i}>{q}</option>)}
                  </select>
                  <span style={LBL}>Your Answer</span>
                  <input type="text" name="securityAnswer" placeholder="Your answer" value={forgotEmailForm.securityAnswer} onChange={handleForgotEmailChange} style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Recover Email</button>
                </form>
                <div style={{ marginTop: 22, textAlign: 'center' }}>
                  <span style={LNK} onClick={() => setFormStep('forgot')}>← Back to Forgot Password</span>
                </div>
              </div>
            )}

            {/* ── ARTIST SECURITY ── */}
            {formStep === 'artist-security' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Verification</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 24px', lineHeight: 1.15 }}>Security Question</h1>
                <form onSubmit={handleArtistSecurityVerify}>
                  <span style={LBL}>Select your security question</span>
                  <select value={selectedSecurityIdx} onChange={e => setSelectedSecurityIdx(e.target.value)} required style={SEL}>
                    <option value="">Select a question</option>
                    {artistSecurityQuestions.map((q, i) => <option key={i} value={q.questionIdx}>{SECURITY_QUESTIONS[q.questionIdx] || 'Question'}</option>)}
                  </select>
                  <span style={LBL}>Your Answer</span>
                  <input type="text" placeholder="Your answer" value={securityAnswer} onChange={e => setSecurityAnswer(e.target.value)} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Verify</button>
                </form>
                <div style={{ marginTop: 22, textAlign: 'center' }}>
                  <span style={LNK} onClick={() => setFormStep('forgot')}>← Back</span>
                </div>
              </div>
            )}

            {/* ── USER SECURITY ── */}
            {formStep === 'user-security' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Verification</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 24px', lineHeight: 1.15 }}>Security Question</h1>
                <form onSubmit={handleUserSecurityVerify}>
                  <span style={LBL}>Select your security question</span>
                  <select value={selectedUserSecurityIdx} onChange={e => setSelectedUserSecurityIdx(e.target.value)} required style={SEL}>
                    <option value="">Select a question</option>
                    {userSecurityQuestions.map((q, i) => <option key={i} value={q.questionIdx}>{SECURITY_QUESTIONS[q.questionIdx] || 'Question'}</option>)}
                  </select>
                  <span style={LBL}>Your Answer</span>
                  <input type="text" placeholder="Your answer" value={userSecurityAnswer} onChange={e => setUserSecurityAnswer(e.target.value)} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Verify</button>
                </form>
                <div style={{ marginTop: 22, textAlign: 'center' }}>
                  <span style={LNK} onClick={() => setFormStep('forgot')}>← Back</span>
                </div>
              </div>
            )}

            {/* ── RESET (security question flow) ── */}
            {formStep === 'reset' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Almost there</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 24px', lineHeight: 1.15 }}>Reset Password</h1>
                <form onSubmit={handleResetSubmit}>
                  <span style={LBL}>New Password</span>
                  <input type="password" name="password" placeholder="New password" value={resetPassword.password} onChange={handleResetChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <span style={LBL}>Confirm New Password</span>
                  <input type="password" name="confirmPassword" placeholder="Re-enter new password" value={resetPassword.confirmPassword} onChange={handleResetChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Update Password</button>
                </form>
                <div style={{ marginTop: 22, textAlign: 'center' }}>
                  <span style={LNK} onClick={() => setFormStep('login')}>← Back to Sign In</span>
                </div>
              </div>
            )}

            {/* ── RESET (email link / token flow) ── */}
            {formStep === 'reset-token' && (
              <div>
                <p style={{ color: '#818cf8', fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '0.75rem', letterSpacing: 1, margin: '0 0 8px', textTransform: 'uppercase' }}>Almost there</p>
                <h1 style={{ fontFamily: 'Gilroy-semiBold, sans-serif', fontSize: '2rem', color: '#fff', margin: '0 0 8px', lineHeight: 1.15 }}>Reset Password</h1>
                {resetTokenError && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', color: '#fca5a5', fontFamily: 'Poppins, sans-serif', fontSize: '0.87rem', marginBottom: 20 }}>
                    {resetTokenError}
                  </div>
                )}
                <form onSubmit={handleResetTokenSubmit}>
                  <span style={LBL}>Email</span>
                  <input type="email" placeholder="Enter your email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <span style={LBL}>New Password</span>
                  <input type="password" name="password" placeholder="New password" value={resetPassword.password} onChange={handleResetChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <span style={LBL}>Confirm New Password</span>
                  <input type="password" name="confirmPassword" placeholder="Re-enter new password" value={resetPassword.confirmPassword} onChange={handleResetChange} required style={INP}
                    onFocus={e => e.target.style.borderColor='#818cf8'} onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.1)'} />
                  <button type="submit" style={SUBMIT_BTN}
                    onMouseEnter={e => e.currentTarget.style.transform='scale(1.02)'}
                    onMouseLeave={e => e.currentTarget.style.transform='scale(1)'}>Update Password</button>
                </form>
                <div style={{ marginTop: 22, textAlign: 'center' }}>
                  <span style={LNK} onClick={() => { setFormStep('login'); navigate('/login'); }}>← Back to Sign In</span>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <style>{`
        .login-left-panel { display: flex !important; }
        .login-mobile-brand { display: none !important; }
        @media (max-width: 820px) {
          .login-left-panel { display: none !important; }
          .login-mobile-brand { display: flex !important; }
        }
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 100px #111827 inset !important;
          -webkit-text-fill-color: #f1f5f9 !important;
          caret-color: #f1f5f9;
        }
        input::placeholder { color: #334155 !important; }
        select option { background: #1e293b; color: #f1f5f9; }
      `}</style>

      <GoogleProfileCompletionModal
        open={showProfileModal}
        missingFields={profileModalFields}
        initialValues={profileModalInitial}
        onClose={() => setShowProfileModal(false)}
        onSubmit={handleGoogleProfileModalSubmit}
      />
    </>
  );
};

const GOOGLE_CLIENT_ID = '975199237692-i5hc087qe2nb69dlu7f310bjp4m2hirl.apps.googleusercontent.com';

const NewLoginWrapped = () => (
  <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <NewLogin />
  </GoogleOAuthProvider>
);

export default NewLoginWrapped;
